import { ERROR_CODES } from './constants.js'
import type { WaitMapItem } from './types.js'

export interface WaitMap {
  addRequest(args: {
    request_id: string
    resolve: (v: unknown) => void
    reject: (e: Error) => void
    timeout: number
    locator: string
  }): void
  resolveRequest(request_id: string, data: unknown): void
  rejectRequest(request_id: string, error_code: number, error_message: string): void
  getPendingCount(): number
  getPendingRequestIds(): string[]
}

export default (): WaitMap => {
  const waitMap: Record<string, WaitMapItem> = {}

  const addRequest = ({
    request_id,
    resolve,
    reject,
    timeout,
    locator,
  }: {
    request_id: string
    resolve: (v: unknown) => void
    reject: (e: Error) => void
    timeout: number
    locator: string
  }): void => {
    if (!request_id) {
      throw new Error('request_id is required')
    }

    waitMap[request_id] = { resolve, reject, locator }

    waitMap[request_id].timeoutId = setTimeout(() => {
      rejectRequest(request_id, ERROR_CODES.TIMEOUT, `request timeout after ${timeout}ms`)
    }, timeout)
  }

  const resolveRequest = (request_id: string, data: unknown): void => {
    if (!request_id) {
      throw new Error('request_id is required')
    }

    const waitItem = waitMap[request_id]
    if (!waitItem) return

    delete waitMap[request_id]

    waitItem.resolve(data)

    clearTimeout(waitItem.timeoutId)
  }

  const rejectRequest = (request_id: string, error_code: number, error_message: string): void => {
    if (!request_id) {
      throw new Error('request_id is required')
    }

    const waitItem = waitMap[request_id]
    if (!waitItem) return

    delete waitMap[request_id]

    const err = new Error(`${waitItem.locator}: ${error_message}`) as Error & { code?: number }
    err.code = error_code

    waitItem.reject(err)

    clearTimeout(waitItem.timeoutId)
  }

  const getPendingCount = (): number => Object.keys(waitMap).length
  const getPendingRequestIds = (): string[] => Object.keys(waitMap)

  return { addRequest, resolveRequest, rejectRequest, getPendingCount, getPendingRequestIds }
}
